import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { LocalAudioTrack, LocalVideoTrack, Track } from 'livekit-client';
import { CANVAS_FPS, MEDIA_TRACK_PREFIX, nextSourceIndex } from '@liveforge/shared';
import { useStudioStore } from '../stores/studio-store';

/**
 * Video files the host rolls into the show.
 *
 * The file only exists on this machine, but the picture has to reach guests and
 * Egress - so it is **published into the room like a camera**: a detached
 * `<video>` element plays it, `captureStream()` turns that into tracks, and
 * from there it is indistinguishable from any other source. Nothing downstream
 * needs to know a file was involved.
 *
 * That also settles synchronisation, by removing it: a clip is a live stream
 * like any other, so every renderer is watching the same frame at the same
 * moment. There is no playback position to keep in step, because there is only
 * one player and everyone else receives its output.
 *
 * The element stays out of the document. It is a decoder, not UI - but it is
 * deliberately left audible, because it is the only way the host hears what
 * they are rolling.
 */

/**
 * `captureStream` is standard and shipping everywhere this app runs, but it is
 * still absent from TypeScript's DOM library, so the element is narrowed here
 * rather than asserted at the call site.
 */
interface CapturableVideo extends HTMLVideoElement {
  captureStream: () => MediaStream;
}

/**
 * How a clip is published, and why it is not published like a camera.
 *
 * A clip arrives from `captureStream()` or `getDisplayMedia()`, and both are
 * *screen-shaped* sources as far as the browser is concerned. Left alone it
 * optimises them the way it optimises a slide: it holds the resolution and
 * lets the frame rate collapse, because that is right for text and wrong for
 * a video. The first seconds of every clip were the encoder discovering this.
 *
 * - **`contentHint: 'motion'`** is the one that matters. It is set on the
 *   MediaStreamTrack rather than through LiveKit, which has no API for it.
 * - **The encoding is stated**, so the publication does not have to find its
 *   way up to a sensible bitrate from a conservative default while the clip
 *   is already on air.
 * - **`maintain-framerate`** decides which way to fail: a clip that softens
 *   for a moment is watchable, a clip that stutters is not.
 *
 * **Simulcast is deliberately left on.** Turning it off would let the primary
 * layer start higher, and it would also hand every guest a single 4 Mbps
 * stream - including the ones on mobile data, which this project has verified
 * and cares about. If the ramp is still visible after this, that is the next
 * experiment, and it is one line.
 */
const CLIP_VIDEO_PUBLISH = {
  source: Track.Source.Unknown,
  simulcast: true,
  degradationPreference: 'maintain-framerate',
  videoEncoding: { maxBitrate: 4_000_000, maxFramerate: CANVAS_FPS },
} as const;

/** Screen-shaped sources need telling that they are carrying video. */
function hintMotion(track: MediaStreamTrack): void {
  track.contentHint = 'motion';
}

export interface MediaSource {
  id: string;
  /**
   * A file plays in an element we own; a YouTube clip plays in a tab we cannot
   * touch and arrives as a screen capture. Everything after acquisition is the
   * same, which is why they share this list.
   */
  kind: 'file' | 'youtube';
  /**
   * Numbers the LiveKit track names. Never reused, so removing a clip cannot
   * rename the ones that outlive it and send a remote layer to the wrong feed.
   */
  index: number;
  name: string;
  /**
   * The player tab's channel, for a YouTube clip; null for a file.
   *
   * One per clip, so several can be prepared before a show and cut between -
   * a shared channel would have every open player answering the same command.
   */
  channelKey: string | null;
  /**
   * The YouTube id, for a clip; null for a file. Kept on the source rather
   * than read from the player's report, because the manifest has to be able to
   * describe a clip whose tab is not open - which, after a reload, is all of
   * them.
   */
  videoId: string | null;
  /** The decoder, for a file. Null for a capture, which has no element here. */
  element: CapturableVideo | null;
  /** What the card shows: the decoder itself, or the captured track attached. */
  preview: HTMLVideoElement | null;
  videoTrack: LocalVideoTrack;
  audioTrack: LocalAudioTrack | null;
  playing: boolean;
  /** Seconds. */
  position: number;
  duration: number;
  loop: boolean;
}

/**
 * Reattaching a clip the studio remembers, rather than adding a new one. The
 * index must be the one it had, or a scene placing `media-2` ends up showing a
 * different clip - see the note on the manifest.
 */
export interface RestoreMedia {
  id: string;
  index: number;
}

/** What the card shows, kept apart from the objects React must not diff. */
interface Playback {
  playing: boolean;
  position: number;
  duration: number;
  loop: boolean;
}

export function useMediaSources() {
  const { localParticipant } = useLocalParticipant();
  const [sources, setSources] = useState<MediaSource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const nextIndex = useRef(1);
  /**
   * One above the highest media index the Live has ever used. Raised, never
   * lowered - see the same guard in `use-local-sources.ts`.
   */
  const floor = useStudioStore((state) =>
    nextSourceIndex(
      [...state.attachedSources, ...state.pendingSources].filter(
        (entry) => entry.kind !== 'device',
      ),
    ),
  );
  useEffect(() => {
    if (floor > nextIndex.current) nextIndex.current = floor;
  }, [floor]);
  // Object URLs and elements outlive renders and own real resources, so they
  // are torn down explicitly rather than left to the garbage collector.
  const urls = useRef(new Map<string, string>());

  const update = useCallback((id: string, values: Partial<Playback>) => {
    setSources((current) =>
      current.map((source) => (source.id === id ? { ...source, ...values } : source)),
    );
  }, []);

  /** Answers whether the clip is publishing, which a restore has to know. */
  const addFile = useCallback(
    async (file: File, restore?: RestoreMedia): Promise<boolean> => {
      setBusy(true);
      setError(null);

      const id = restore?.id ?? crypto.randomUUID();
      const url = URL.createObjectURL(file);
      const element = document.createElement('video') as CapturableVideo;
      element.src = url;
      element.loop = false;
      element.playsInline = true;
      // Not muted: this element is the host's monitor as well as the encoder.
      element.volume = 1;

      try {
        await new Promise<void>((resolve, reject) => {
          element.onloadedmetadata = () => resolve();
          element.onerror = () =>
            reject(
              new Error(
                `This browser cannot play ${file.name}. Use MP4 (H.264) or WebM - a container ` +
                  'like MKV will not open however common it is.',
              ),
            );
        });

        // Chrome only produces tracks once something has been decoded, so the
        // stream is taken after the first frame rather than on load.
        await element.play();

        const stream = element.captureStream();
        const [videoSource] = stream.getVideoTracks();
        const [audioSource] = stream.getAudioTracks();
        if (!videoSource) {
          throw new Error('That file produced no video track.');
        }

        const index = restore?.index ?? nextIndex.current;
        if (!restore) nextIndex.current += 1;

        hintMotion(videoSource);
        /*
         * A capture of a 60fps file emits 60fps, and every frame past the rate
         * the composition is drawn at is decoded, encoded, sent, decoded again
         * by Egress and then thrown away. The tab capture already asks for a
         * ceiling in its constraints; a `captureStream()` cannot, so it is
         * asked for afterwards.
         *
         * Best effort on purpose: a browser that will not narrow the track is
         * not a reason to refuse the clip.
         */
        await videoSource
          .applyConstraints({ frameRate: { max: CANVAS_FPS } })
          .catch(() => undefined);
        const videoTrack = new LocalVideoTrack(videoSource);
        const audioTrack = audioSource ? new LocalAudioTrack(audioSource) : null;

        await localParticipant.publishTrack(videoTrack, {
          name: `${MEDIA_TRACK_PREFIX}${index}`,
          ...CLIP_VIDEO_PUBLISH,
        });
        if (audioTrack) {
          await localParticipant.publishTrack(audioTrack, {
            name: `${MEDIA_TRACK_PREFIX}${index}-audio`,
            source: Track.Source.Unknown,
          });
        }

        urls.current.set(id, url);
        setSources((current) => [
          ...current,
          {
            id,
            index,
            kind: 'file',
            channelKey: null,
            videoId: null,
            name: file.name,
            element,
            preview: element,
            videoTrack,
            audioTrack,
            playing: true,
            position: 0,
            duration: Number.isFinite(element.duration) ? element.duration : 0,
            loop: false,
          },
        ]);
        return true;
      } catch (cause) {
        element.pause();
        element.src = '';
        URL.revokeObjectURL(url);
        setError(cause instanceof Error ? cause.message : 'That file could not be opened.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [localParticipant],
  );

  /**
   * Captures another tab - the YouTube player page - and publishes it.
   *
   * Screen capture is the only way to get pixels out of a cross-origin player,
   * and it has to be a **tab** rather than a window: Chrome offers a tab's
   * audio in the picker, and window capture does not. That audio matters, and
   * it is also why the player has a tab of its own - capturing the studio's own
   * tab would take the guests' voices with it.
   *
   * Must be called straight from a click. The picker is a user gesture, and
   * browsers refuse it otherwise.
   */
  const addCapture = useCallback(
    async (
      name: string,
      channelKey: string,
      videoId: string,
      restore?: RestoreMedia,
    ): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          // A ceiling rather than a demand: the browser will not upscale a
          // small window to meet it, but it will stop a 4K monitor from
          // publishing 4K of a video that is going out at 1080p.
          video: {
            frameRate: CANVAS_FPS,
            width: { max: 1920 },
            height: { max: 1080 },
            // Opens the picker on the tab list, which is the only surface that
            // carries audio and therefore the only correct answer here.
            displaySurface: 'browser',
          },
          audio: true,
          // The studio's own tab is never the right choice - capturing it
          // would feed the guests' voices back through the clip - so it is not
          // offered. One fewer wrong thing to click.
          selfBrowserSurface: 'exclude',
          // No "switch to sharing this instead" bar over the broadcast.
          surfaceSwitching: 'exclude',
          systemAudio: 'exclude',
        } as DisplayMediaStreamOptions);
        const [videoSource] = stream.getVideoTracks();
        const [audioSource] = stream.getAudioTracks();
        if (!videoSource) {
          throw new Error('That capture produced no video.');
        }
        if (!audioSource) {
          setError(
            'Captured without audio. Pick the player tab in the dialogue and tick "Also share ' +
              'tab audio" - a window or a screen cannot carry sound.',
          );
        }

        const index = restore?.index ?? nextIndex.current;
        if (!restore) nextIndex.current += 1;

        hintMotion(videoSource);
        const videoTrack = new LocalVideoTrack(videoSource);
        const audioTrack = audioSource ? new LocalAudioTrack(audioSource) : null;

        await localParticipant.publishTrack(videoTrack, {
          name: `${MEDIA_TRACK_PREFIX}${index}`,
          ...CLIP_VIDEO_PUBLISH,
        });
        if (audioTrack) {
          await localParticipant.publishTrack(audioTrack, {
            name: `${MEDIA_TRACK_PREFIX}${index}-audio`,
            source: Track.Source.Unknown,
          });
        }

        setSources((current) => [
          ...current,
          {
            id: restore?.id ?? crypto.randomUUID(),
            index,
            kind: 'youtube',
            channelKey,
            videoId,
            name,
            element: null,
            // `attach` is typed for media generally; a video track hands back a
            // video element, which is the only thing the card can show.
            preview: videoTrack.attach() as HTMLVideoElement,
            videoTrack,
            audioTrack,
            playing: false,
            position: 0,
            duration: 0,
            loop: false,
          },
        ]);
        return true;
      } catch (cause) {
        // A cancelled picker is a decision, not a fault.
        const aborted = cause instanceof DOMException && cause.name === 'NotAllowedError';
        if (!aborted) {
          setError(cause instanceof Error ? cause.message : 'That capture could not be started.');
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [localParticipant],
  );

  const remove = useCallback(
    async (id: string) => {
      const source = sources.find((entry) => entry.id === id);
      if (!source) return;

      await localParticipant.unpublishTrack(source.videoTrack, true).catch(() => undefined);
      if (source.audioTrack) {
        await localParticipant.unpublishTrack(source.audioTrack, true).catch(() => undefined);
      }
      source.element?.pause();
      if (source.element) source.element.src = '';
      if (source.kind === 'youtube') source.videoTrack.detach();

      const url = urls.current.get(id);
      if (url) {
        URL.revokeObjectURL(url);
        urls.current.delete(id);
      }
      setSources((current) => current.filter((entry) => entry.id !== id));
    },
    [localParticipant, sources],
  );

  const play = useCallback(
    (id: string, playing: boolean) => {
      const source = sources.find((entry) => entry.id === id);
      if (!source) return;
      if (source.element) {
        if (playing) void source.element.play();
        else source.element.pause();
      }
      update(id, { playing });
    },
    [sources, update],
  );

  const seek = useCallback(
    (id: string, seconds: number) => {
      const source = sources.find((entry) => entry.id === id);
      if (!source?.element) {
        update(id, { position: seconds });
        return;
      }
      source.element.currentTime = Math.max(0, Math.min(seconds, source.element.duration || 0));
      update(id, { position: source.element.currentTime });
    },
    [sources, update],
  );

  const setLoop = useCallback(
    (id: string, loop: boolean) => {
      const source = sources.find((entry) => entry.id === id);
      if (!source) return;
      if (source.element) source.element.loop = loop;
      update(id, { loop });
    },
    [sources, update],
  );

  // The element is the authority on where playback actually is - a seek can be
  // refused, and the end of a file arrives without anyone asking - so the card
  // follows the element rather than the other way round.
  // Derived rather than read inside: the effect cares only whether there is
  // anything to poll, and depending on the whole list would restart the timer
  // on every tick it caused.
  const hasElement = sources.some((source) => source.element !== null);

  useEffect(() => {
    if (!hasElement) return;

    const timer = window.setInterval(() => {
      setSources((current) =>
        current.map((source) =>
          // A capture has no element to ask; its player reports over the
          // broadcast channel instead, and the card applies that itself.
          source.element
            ? {
                ...source,
                position: source.element.currentTime,
                duration: Number.isFinite(source.element.duration) ? source.element.duration : 0,
                playing: !source.element.paused,
              }
            : source,
        ),
      );
    }, 250);

    return () => window.clearInterval(timer);
  }, [hasElement]);

  // Anything still publishing when the studio closes has to be taken down, or
  // the room keeps a track nobody is driving.
  useEffect(() => {
    const opened = urls.current;
    return () => {
      for (const url of opened.values()) URL.revokeObjectURL(url);
      opened.clear();
    };
  }, []);

  return {
    sources,
    error,
    busy,
    addFile,
    addCapture,
    update,
    remove,
    play,
    seek,
    setLoop,
    clearError: useCallback(() => setError(null), []),
  };
}
